import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, input } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Observable } from 'rxjs';
import { AnagraficaClassificheService } from 'src/app/core/anagrafica-classifiche.service';
import { Classifica } from 'src/app/shared/models/classifica.model';

@Component({
  selector: 'app-anagrafica-classifica',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, TableModule, DialogModule, ButtonModule, InputTextModule,AsyncPipe],
  template: `
    <p-table #table [value]="(items$ | async) ?? []" [paginator]="true" [rows]="10" [alwaysShowPaginator]="false">
  <ng-template pTemplate="header">
    <tr>
      <th colspan="2">Classifica</th>
      <th rowspan="2">
        <button class="addButton" pButton type="button" icon="fas fa-plus" (click)="openModal('new')"
          title="Aggiungi" [disabled]="!canModified()"></button>
      </th>
    </tr>
    <tr>
      <th colspan="2">
        <input pInputText type="text" placeholder="Filtra..." style="width:100%"
          (input)="table.filter($any($event.target).value || '', 'nomeClassifica', 'contains')">
      </th>
    </tr>
  </ng-template>
  <ng-template pTemplate="body" let-item>
    <tr [pSelectableRow]="item">
      <td>{{ item.sigla }}</td>
      <td>{{ item.nomeClassifica }}</td>
      <td class="w-7rem">
        <button pButton type="button" style="margin-right: 5px;" icon="fas fa-pen" class="p-button-warning"
          title="Modifica" (click)="openModal('edit', item)" [disabled]="!canModified()"></button>
        <button pButton type="button" icon="fas fa-times" class="p-button-danger" title="Elimina"
          (click)="removeItem(item)" [disabled]="!canModified()"></button>
      </td>
    </tr>
  </ng-template>
  <ng-template pTemplate="emptymessage">
    <tr>
      <td [attr.colspan]="2">
        Nessun elemento
      </td>
    </tr>
  </ng-template>
</p-table>

<p-dialog [(visible)]="isDialogOpen" [modal]="true" [closable]="false" [closeOnEscape]="false">
  <p-header>
    @switch(editMode){
      @case ('new'){
        <span>Aggiungi Classifica</span>
      }
      @case ('edit'){
        <span>Modifica Classifica</span>
      }
    }
  </p-header>
  <form [formGroup]="form">
    <div class="card grid">
      <div class="col-3" style="margin-top:6px;">Sigla*</div>
      <div class="col-9">
        <input id="sigla" type="text" pInputText formControlName="sigla" maxlength="2" class="w-3rem">
      </div>
    </div>
    <div class="card grid">
      <div class="col-3" style="margin-top:6px;">Nome*</div>
      <div class="col-9">
        <input id="name" type="text" pInputText formControlName="name">
      </div>
    </div>
  </form>

  <p-footer>
    <button pButton type="button" label="Annulla" class="p-button-secondary" (click)="resetForm()"></button>
    <button pButton type="button"
      [label]="editMode === 'new' ? 'Aggiungi': 'Modifica'" (click)="saveItem()"
      [disabled]="form.invalid"></button>
  </p-footer>
</p-dialog>
  `,
  styles: ``
})
export class AnagraficaClassificaComponent {

  private readonly classificheService = inject(AnagraficaClassificheService);
  private messageService = inject(MessageService);

  public canModified = input<boolean>(true);

  public items$: Observable<Classifica[]>;
  public editMode: 'new' | 'edit' | undefined;
  public form: FormGroup;
  public itemInEdit: Classifica | null = null;
  public isDialogOpen = false;

  constructor(){
    this.items$ = this.classificheService.getAll();

    this.form = new FormGroup({
      sigla: new FormControl(null, [Validators.required, Validators.maxLength(2), Validators.pattern('[A-Z\d]{1,2}')]),
      name: new FormControl(null, Validators.required)
    });
  }

  public openModal(mode: 'new' | 'edit', item?: Classifica): void {
    this.isDialogOpen = true;
    this.editMode = mode;
    this.itemInEdit = item ?? null;
    this.form.controls.sigla.setValue(item ? item.sigla : null);
    this.form.controls.name.setValue(item ? item.nomeClassifica : null);
  }

  public saveItem(): void {
    if (this.editMode === 'new') {
      const item: Classifica = {
        sigla: (this.form.controls.sigla.value as string).toUpperCase(),
        nomeClassifica: this.form.controls.name.value as string
      };

      this.classificheService.create(item).subscribe(
        () => this.handleSuccess('Creazione'),
        err => this.handleError(err)
      );
    } else {
      const item = { ...this.itemInEdit } as any;
      item.sigla = (this.form.controls.sigla.value as string).toUpperCase();
      item.nomeClassifica = this.form.controls.name.value as string;

      this.classificheService.update(item).subscribe(
        () => this.handleSuccess('Modifica'),
        err => this.handleError(err)
      );
    }
  }

  public removeItem(item: Classifica): void {
    this.classificheService.delete(item).subscribe({
      next: () => this.handleSuccess('Eliminazione'),
      error: (err: Error) => {
        if (err instanceof HttpErrorResponse && err.status === 409) {
          this.message('error', 'Errore', err.error?.message || err.error);
        } else {
          this.handleError(err);
        }
      }
    });
  }

  public resetForm(): void {
    this.isDialogOpen = false;
    this.editMode = undefined;
    this.itemInEdit = null;
    this.form.reset();
  }

  private handleSuccess(operation: string) {
    this.message('success', operation, `${operation} classifica completata`);
    this.resetForm();
  }

  private handleError(err: Error): void {
    if (err instanceof HttpErrorResponse && err.status === 409) {
      this.message('error', 'Errore', err.error);
    } else {
      this.message('error', 'Errore', `Si è verificato un errore. Operazione non completata`);
      this.resetForm();
    }
  }

  private message(sev: string, sum: string, det: string) {
    this.messageService.add({
      key: 'toast',
      severity: sev,
      summary: sum,
      detail: det
    });
  }
}
